import { SummaryCards_CheckoutConfigFragment } from "src/pages/configurator/components/SummaryCards.generated";
import {
  findChildOptionGroup,
  maybeStripRoomNumber,
  shouldRollChildPriceIntoParent,
  shouldUseSwatchSelector,
} from "src/pages/configurator/Configurator.utils";
import { AddAduOptionCode } from "src/pages/configurator/stepTemplates/Adu.step";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { getProgramDataStrings } from "./plan.utils";
import {
  SummaryPrint_CheckoutConfigOptionGroupFragment,
  SummaryUtils_CheckoutConfigOptionGroupFragment,
} from "./summary.utils.generated";

export function getSummaryPlanDescription(
  checkoutConfig: Pick<SummaryCards_CheckoutConfigFragment, "productOffering" | "programData" | "isReversed">,
): {
  planName: string;
  planProgramData: string;
} {
  const { productOffering, programData, isReversed } = checkoutConfig;

  const { bedrooms, bathrooms, sqft } = getProgramDataStrings(programData);

  if (!productOffering) {
    return { planName: "", planProgramData: "" };
  }

  return {
    planName: `${productOffering.name} (Orientation: ${isReversed ? "Reversed" : "Standard"})`,
    planProgramData: `${bedrooms}, ${bathrooms} & ${sqft}`,
  };
}

/**
 * ADU option groups have their own summary section, so main-house sections must filter them out.
 *
 * Can't be inferred from subStepNumber: the BE computes an ADU RPOG's step/substep with `ignoreAdu`, then overwrites
 * only the step with `Adu` — so ADU groups keep a main-house substep (Interior Scheme/Spec Level land on substep 1,
 * "Interior Package"). Step is the only reliable main-house vs ADU signal.
 */
export function isAduOptionGroup(ccog: Pick<SummaryUtils_CheckoutConfigOptionGroupFragment, "step">): boolean {
  return ccog.step === CheckoutConfigOptionGroupStep.Adu;
}

export function filterSelectedOptionsBySteps(
  optionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
  steps: CheckoutConfigOptionGroupStep[],
) {
  return (
    optionGroups
      // showOnCustomerSummary, not isEnabled — see filterSummaryEffectiveOptionGroups for why isEnabled is wrong here.
      .filter((ccog) => ccog.showOnCustomerSummary && steps.includes(ccog.step))
      .map((ccog) => ccog.selectedOption)
      .compact()
  );
}

// ----- Effective summary helpers -----
// effective summary items are items that may have been part of the base config but were deselected by the user or
// were optional option groups that were not selected by the user.
//
// Visibility is gated on showOnCustomerSummary, NOT isEnabled: isEnabled flips false for personalization groups once a
// config is reserved/expired (takeoff slots close), which silently drops real selections (e.g. Bathroom Sconce, ADU doors) from
// the summary. showOnCustomerSummary is the reserve-stable "not a Required Add-On and still in scope" flag.
export function filterSummaryEffectiveOptionGroups(
  optionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
  steps: CheckoutConfigOptionGroupStep[],
) {
  return optionGroups.filter(
    (ccog) => ccog.showOnCustomerSummary && steps.includes(ccog.step) && !!ccog.selectedOption,
  );
}

/**
 * Main-house interior-personalization option groups that should appear on the customer summary, grouped by checkout
 * sub-step number. Shared by the Home Summary screen (SummaryCards) and the customer PDF (CustomerSummary.report)
 */
export function getInteriorPersonalizationGroupsBySubStep(
  optionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
): Record<number, SummaryUtils_CheckoutConfigOptionGroupFragment[]> {
  return (
    optionGroups
      // Skip ADU groups: their main-house substep (see isAduOptionGroup) otherwise renders them in the main-house
      // room sections on top of the ADU section, i.e. twice.
      .filter(
        (ccog) =>
          !isAduOptionGroup(ccog) && !!ccog.subStepNumber && !!ccog.selectedOption && ccog.showOnCustomerSummary,
      )
      .filter((ccog) => !(ccog.parentGroup && shouldUseSwatchSelector(ccog)))
      .groupBy((ccog) => ccog.subStepNumber!)
  );
}

export function getSummaryGroupsEffectivePriceInCents(
  optionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
): number {
  return optionGroups.sum((ccog) => ccog.selectedOption?.totalPriceInCents);
}

/**
 * Finds all downstream option groups included by the given option group's selected option by checking allOptionGroups entry.includedByRoot.id
 */
export function getDownstreamIncludedOptionGroups(
  optionGroup: SummaryPrint_CheckoutConfigOptionGroupFragment | undefined,
  allOptionGroups: SummaryPrint_CheckoutConfigOptionGroupFragment[],
): SummaryPrint_CheckoutConfigOptionGroupFragment[] {
  const selectedOptionId = optionGroup?.selectedOption?.id;
  if (!selectedOptionId) return [];

  // Find all groups whose selectedOption.includedByRoot points to our selected option
  // Note: we skip the `isEnabled` check intentionally to ensure that anything automatically applied to the config is caught
  // and shown on the summary. Much like how our summary step Interior Personalization section only cares about having a selected option.
  const includedGroups = allOptionGroups.filter((ccog) => ccog.selectedOption?.includedByRoot?.id === selectedOptionId);
  return includedGroups;
}

/** Checks whether any option group has the ADU add-on selected. ADU *everything* should have this as a pre-req */
export function legacyHasSelectedAduOption(
  optionGroups: Pick<SummaryUtils_CheckoutConfigOptionGroupFragment, "selectedOption">[],
): boolean {
  // Note: Checking the option code instead of AduStep + hasSelectedOption because of a bug where some
  // linked personalization options are being shown on the ADU step instead of Addt'l options where they have a selected
  // option applied by the BE defaultIf logic.
  return optionGroups.some((ccog) => ccog.selectedOption?.option.code === AddAduOptionCode);
}

export function buildSummaryListEffectiveLineItems(
  optionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
  // if true, will include the location name in the title.
  withLocation: boolean = false,
  // The full set of config groups, used to find a parent's rolled-up swatch child so it can be nested below.
  allOptionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[] = optionGroups,
): { title: string; priceInCents: number; optionCode: string }[] {
  // One line for a group's selected option, at its own price.
  const lineItem = (ccog: SummaryUtils_CheckoutConfigOptionGroupFragment) => {
    const option = ccog.selectedOption!.option;
    const locationName =
      withLocation && ccog.group.readyPlanLocation
        ? ` - ${maybeStripRoomNumber(ccog.group.readyPlanLocation.marketingName)}`
        : "";
    return {
      title: `${option.name}${locationName}`,
      priceInCents: ccog.selectedOption!.totalPriceInCents,
      optionCode: option.code,
    };
  };
  return optionGroups.flatMap((ccog) => {
    // A selected swatch child is surfaced beneath its parent (below), never as its own top-level line.
    if (ccog.parentGroup && shouldUseSwatchSelector(ccog)) return [];
    if (!ccog.selectedOption?.option) return [];
    // Mirror the step: a swatch child's price rolls into the parent's section total. We still surface the child's
    // selected option as its own nested line (at its own price) so the user can see which swatch they picked.
    const childGroup = findChildOptionGroup(ccog, allOptionGroups);
    const rollsUpChild = !!childGroup && shouldRollChildPriceIntoParent(childGroup, childGroup.selectedOption);
    return rollsUpChild ? [lineItem(ccog), lineItem(childGroup!)] : [lineItem(ccog)];
  });
}
